/* Variant availability for AMP gifts. AMP remains responsible for adding gifts. */
(function () {
  'use strict';

  window.PtvCreateGiftAvailability = function (config) {
    const allowedIds = new Set(config.variantIds.map(String));
    const disabledIds = new Set(config.disabledVariantIds.map(String));
    const cache = new Map();
    const originalOptions = new WeakMap();
    const MAX_AGE = 30000;
    const RETRY_DELAY = 5000;
    let pendingClick = false;
    let pendingTask = Promise.resolve();
    let replayButton = null;
    let started = false;

    function id(value) {
      const match = String(value || '').match(/(?:^|\/)(\d+)$/);
      return match ? match[1] : null;
    }

    function normalizeTitle(value) {
      return String(value || '').replace(/^\s*FREE\s+/i, '').trim().toUpperCase();
    }

    function parse(value) {
      try { return typeof value === 'string' ? JSON.parse(value) : value; }
      catch (_) { return null; }
    }

    function products() {
      let state;
      try { state = window.SLIDECART_STATE && window.SLIDECART_STATE(); }
      catch (_) { return []; }
      if (!state) return [];
      const result = new Map();
      function include(group) {
        const data = parse(group);
        if (!data || !Array.isArray(data.items)) return;
        data.items.forEach(function (product) {
          const productId = id(product.id);
          if (!productId || !product.handle || !Array.isArray(product.variants)) return;
          const ids = product.variants.map(function (variant) { return id(variant.id); })
            .filter(function (variantId) { return allowedIds.has(variantId) && !disabledIds.has(variantId); });
          if (!ids.length) return;
          const existing = result.get(productId);
          result.set(productId, {
            id: productId, handle: product.handle, title: product.title,
            ids: new Set((existing ? Array.from(existing.ids) : []).concat(ids))
          });
        });
      }
      ((state.settings && state.settings.rewards_tiers) || []).forEach(function (tier) { include(tier.free_gifts); });
      (state.gifts || []).forEach(function (gift) { include(gift.customerGets); });
      return Array.from(result.values());
    }

    function identify(item) {
      if (!item) return null;
      const select = item.querySelector('select[id^="productInfoVariant"]') || item.querySelector('select');
      const options = select ? Array.from(select.options) : [];
      const productId = select && select.id.replace(/^productInfoVariant/, '');
      const entries = products();
      let matches = entries.filter(function (product) {
        return product.id === productId || options.some(function (option) { return product.ids.has(id(option.value)); });
      });
      // The optional stacked layout omits its select for a single configured variant.
      if (!matches.length && !select) {
        const title = item.querySelector('.free-gifts-item-body > span, .free-gifts-title');
        matches = entries.filter(function (product) {
          return title && product.ids.size === 1 && normalizeTitle(product.title) === normalizeTitle(title.textContent);
        });
      }
      if (matches.length !== 1) return null;
      if (!select) item.setAttribute('data-variant-id', Array.from(matches[0].ids)[0]);
      return { product: matches[0], select: select, options: options };
    }

    function selectedId(info) {
      return info.select ? id(info.select.value) : Array.from(info.product.ids)[0];
    }

    function fresh(entry) {
      return !!entry && !!entry.availableIds && Date.now() - entry.checkedAt < MAX_AGE;
    }

    function read(product, force) {
      let entry = cache.get(product.handle);
      if (!entry) {
        entry = { availableIds: null, checkedAt: 0, attemptedAt: 0, pending: null };
        cache.set(product.handle, entry);
      }
      if (entry.pending) return entry.pending;
      if (!force && (fresh(entry) || (!entry.availableIds && Date.now() - entry.attemptedAt < RETRY_DELAY))) return Promise.resolve(entry);
      entry.attemptedAt = Date.now();
      const controller = new AbortController();
      const timeout = setTimeout(function () { controller.abort(); }, 6000);
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const url = root.replace(/\/?$/, '/') + 'products/' + encodeURIComponent(product.handle) + '.js?_ptv_stock=' + Date.now();
      entry.pending = (async function () {
        try {
          const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
          if (!response.ok) throw new Error('Gift product unavailable');
          const data = await response.json();
          if (id(data.id) !== product.id || !Array.isArray(data.variants)) throw new Error('Invalid gift product');
          entry.availableIds = new Set(data.variants.filter(function (variant) {
            return variant.available === true;
          }).map(function (variant) { return id(variant.id); }));
          entry.checkedAt = Date.now();
        } catch (_) {
          // Never retain a successful stale result after a failed fresh check.
          entry.availableIds = null;
          entry.checkedAt = 0;
        } finally {
          clearTimeout(timeout);
          entry.pending = null;
          config.onChange();
        }
        return entry;
      })();
      return entry.pending;
    }

    function inspect(item) {
      const info = identify(item);
      let available = false;
      let variantId = null;
      if (info) {
        read(info.product, false);
        const entry = cache.get(info.product.handle);
        // Keep the last confirmed options visible while a background refresh runs.
        // Unknown/failed checks have no availableIds; redemption still requires fresh stock.
        const confirmed = !!entry && !!entry.availableIds;
        const usable = function (value) {
          return confirmed && info.product.ids.has(value) && entry.availableIds.has(value);
        };
        info.options.forEach(function (option) {
          if (!originalOptions.has(option)) originalOptions.set(option, { hidden: option.hidden, disabled: option.disabled });
          const original = originalOptions.get(option);
          const unavailable = !usable(id(option.value));
          // Keep option nodes so AMP/React and the tier guard retain their variant IDs.
          option.hidden = original.hidden || unavailable;
          option.disabled = original.disabled || unavailable;
        });
        variantId = selectedId(info);
        if (confirmed && info.select && !info.options.some(function (option) {
          return id(option.value) === variantId && !option.disabled && !option.hidden;
        })) {
          const first = info.options.find(function (option) { return !option.disabled && !option.hidden; });
          if (first) {
            info.select.value = first.value;
            // AMP's React handler updates the variant used by its native gift add.
            info.select.dispatchEvent(new Event('change', { bubbles: true }));
            variantId = id(first.value);
          }
        }
        available = !!usable(variantId) && (!info.select || info.options.some(function (option) {
          return id(option.value) === variantId && !option.disabled && !option.hidden;
        }));
      }
      item.querySelectorAll('.free-gifts-add-button').forEach(function (button) {
        button.dataset.ptvAvailabilityBlocked = available ? '0' : '1';
      });
      return { available: available, variantId: variantId };
    }

    function authorizeClick(event, button) {
      if (replayButton === button) {
        replayButton = null;
        return true;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      if (pendingClick) return false;
      const item = button.closest('.free-gifts-item');
      const info = identify(item);
      if (!info) return false;
      const requestedId = selectedId(info);
      pendingClick = true;
      button.setAttribute('aria-busy', 'true');
      pendingTask = (async function () {
        try {
          const entry = await read(info.product, true);
          const status = inspect(item);
          config.onChange();
          // Allow React to commit any selection update before touching its button.
          await new Promise(function (resolve) { setTimeout(resolve, 0); });
          const latest = identify(item);
          if (!button.isConnected || !latest || !fresh(entry) || !entry.availableIds.has(requestedId) ||
              !status.available || item.hidden ||
              selectedId(latest) !== requestedId || status.variantId !== requestedId ||
              !config.canReplay(item)) return;
          // A vanished flavour is replaced in the UI, but is never silently redeemed.
          replayButton = button;
          button.click();
        } finally {
          replayButton = null;
          pendingClick = false;
          button.removeAttribute('aria-busy');
        }
      })().catch(function () { config.onChange(); });
      return false;
    }

    function refresh() {
      if (document.hidden) return;
      // Hidden sold-out cards are included so replenished stock can return.
      cache.forEach(function (entry) { entry.checkedAt = 0; });
      config.onChange();
    }

    function start() {
      if (started) return;
      started = true;
      window.addEventListener('focus', refresh);
      window.addEventListener('pageshow', refresh);
      document.addEventListener('visibilitychange', refresh);
      ['cart:open', 'cart:opened', 'ajaxCart:open', 'slidecart:open'].forEach(function (name) {
        document.addEventListener(name, refresh);
      });
      setInterval(function () {
        const panel = document.querySelector('#slidecarthq .slidecarthq.open, #slidecarthq.slidecarthq.open');
        if (!document.hidden && panel) config.onChange();
      }, MAX_AGE);
    }

    return {
      inspect: inspect, authorizeClick: authorizeClick, refresh: refresh, start: start,
      whenIdle: function () { return pendingTask; }
    };
  };
})();
